import { cn } from '@ifixit/ui/lib/utils';
import { useEffect } from 'react';
import type { HTMLAttributes } from 'react';

const INTEGRATION_SCRIPT_SRC = 'https://widgets.trustedshops.com/integration/integration.js';

export const TRUSTED_SHOPS_BADGE_FLAG = 'trusted-shops-badge';

// Per-store Trusted Shops integration IDs for the inline Trustbadge
// (https://github.com/iFixit/ifixit/issues/61178#issuecomment-4683958599).
// Each ID points at a remotely-managed widget config (the same
// inline-horizontal widget for every store) on
// trustbadge-config-api.trustedshops.com. The Next.js dev
// locales (eu_test, dev) map to the same integrations as their prod
// equivalents (eu, fr) so the badge is reachable in local dev without
// callers having to normalize.
const integrationIdByStoreCode: Record<string, string> = {
   eu: 'int-0534e257-f4e0-4f4b-97c4-b6309f6f9228',
   eu_test: 'int-0534e257-f4e0-4f4b-97c4-b6309f6f9228',
   de: 'int-bd86399e-13ea-4e55-9960-0e0b3fed3e24',
   fr: 'int-2c92a1de-0939-454b-89d2-318ba8d64d57',
   dev: 'int-2c92a1de-0939-454b-89d2-318ba8d64d57',
   uk: 'int-7a0dd964-c015-4376-8d8a-ebf38b1be629',
};

export function isTrustedShopsStore(storeCode: string): boolean {
   return storeCode in integrationIdByStoreCode;
}

declare module 'react' {
   // eslint-disable-next-line @typescript-eslint/no-namespace
   namespace JSX {
      interface IntrinsicElements {
         'minimized-trustbadge-inline-horizontal': HTMLAttributes<HTMLElement>;
      }
   }
}

export function TrustedShopsBadge({
   storeCode,
   className,
}: {
   storeCode: string;
   className?: string;
}) {
   const integrationId = integrationIdByStoreCode[storeCode];

   useEffect(() => {
      if (!integrationId) return;
      const script = document.querySelector<HTMLScriptElement>(
         `script[src="${CSS.escape(INTEGRATION_SCRIPT_SRC)}"]`
      );
      if (!script) {
         const newScript = document.createElement('script');
         newScript.type = 'module';
         newScript.src = INTEGRATION_SCRIPT_SRC;
         newScript.dataset.integrationId = integrationId;
         document.head.append(newScript);
      } else if (script.dataset.integrationId !== integrationId) {
         // integration.js can only show one shop per page, so a new store needs a
         // new page. Store switches load one through handleLocaleLinkClick (@ifixit/ui);
         // this catches the rest, like the workbench's in-place refresh.
         window.location.reload();
      }
   }, [integrationId]);

   if (!integrationId) {
      return null;
   }

   return (
      <div className={cn('mt-6 flex justify-center', className)}>
         <minimized-trustbadge-inline-horizontal />
      </div>
   );
}
